import { useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import { zoomToCursorState } from '~/state/atoms/toolsAtoms';
import {
    environmentState,
    secondaryEnvironmentState,
    viewManagerState,
} from '~/state/atoms/engineAtoms';
import useViewManagerEvents from '~/hooks/useViewManagerEvents';
import { areEnvironmentsLockedState } from '~/state/atoms/viewerAtoms';

/**
 * Hook that synchronizes zoom-related functionality across environments.
 * - Syncs zoom scale between locked environments.
 * - Updates zoom-to-cursor behavior dynamically.
 */
export function useZoomHandler() {
    const environment = useRecoilValue(environmentState);
    const secondaryEnvironment = useRecoilValue(secondaryEnvironmentState);
    const viewManager = useRecoilValue(viewManagerState);
    const areEnvironmentsLocked = useRecoilValue(areEnvironmentsLockedState);
    const zoomToCursor = useRecoilValue(zoomToCursorState);

    const { handleZoomScaleSync } = useViewManagerEvents();

    useEffect(() => {
        if (!viewManager) {
            return;
        }

        if (areEnvironmentsLocked) {
            viewManager.addEventListener(
                'zoomScaleChanged',
                handleZoomScaleSync
            );
        }
        return () => {
            viewManager?.removeEventListener(
                'zoomScaleChanged',
                handleZoomScaleSync
            );
        };
    }, [areEnvironmentsLocked, viewManager, handleZoomScaleSync]);

    useEffect(() => {
        if (!environment) {
            return;
        }

        environment.panZoom.setZoomToCursor(zoomToCursor);
        if (secondaryEnvironment) {
            secondaryEnvironment.panZoom.setZoomToCursor(
                environment.panZoom.zoomToCursor
            );
        }
    }, [environment, secondaryEnvironment, zoomToCursor]);
}
